import { useMemo } from "react";
import {
  getTag,
  getRsiSignal,
  getMacdSignal,
  getStochasticSignal,
  getSmaSignal,
  getSmaSignalValue,
} from "../components/DetailsCommon/Forecast/utils/formatSignal";
import {
  SignalLevelType,
  SmaSignal,
} from "../components/DetailsCommon/Forecast/constants";
import { signalLevelMap } from "../components/DetailsCommon/Forecast/utils/signalLevelMap";
import {
  trendAnalysisCalc,
  TrendAnalysisResult,
} from "../utils/trendAnalysisCalc";
import { computeSignals } from "@utils/technicalAnalysisCalc";

export interface IndicatorData {
  id?: string;
  indicator?: string;
  signal?: string;
  value?: string | number;
  sentiment?: "Bullish" | "Bearish" | "Neutral";
  signalValue?: string;
}

export interface SignalDataType {
  buySignal: number;
  sellSignal: number;
  neutralSignal: number;
}

export interface IndicatorDataResponse {
  indicatorData: IndicatorData[];
  macd?: {
    signal: string;
    value: string;
  };
  rsi?: {
    signal: string;
    value: string;
  };
  stochasticK?: {
    signal: string;
    value: string;
  };
  smaSignal?: {
    [key: string]: string;
  };
  signalData?: SignalDataType;
  signalLevel?: SignalLevelType;
  trendAnalysis?: TrendAnalysisResult;
  trendUpDownResult?: "uptrend" | "downtrend";
}

interface UseTechnicalIndicatorsProps {
  symbol?: string;
  isStock?: boolean;
  ohlcv?: any; // 价格数据数组，用于计算指标值
  currentPrice?: number; // 当前价格
}

/**
 * 技术指标数据构造hooks
 * 根据是否是股票来区分使用SMA或EMA
 * @param symbol 股票/代币符号
 * @param isStock 是否为股票
 * @param priceData 价格数据数组
 * @param currentPrice 当前价格
 * @returns 技术指标数据数组
 */
export const useTechnicalIndicators = ({
  isStock = false,
  ohlcv = [],
}: UseTechnicalIndicatorsProps): IndicatorDataResponse => {
  // 根据数据长度动态设置SMA周期
  const dataLength = ohlcv.length;
  let smaPeriods = [5, 10, 20, 30, 60];

  // 如果数据不足60个点，调整SMA周期
  if (dataLength < 60) {
    if (dataLength < 30) {
      smaPeriods = [5, 10, 20];
    } else if (dataLength < 60) {
      smaPeriods = [5, 10, 20, 30];
    }
  }

  const numberOhlc = ohlcv.map((item: any) => ({
    ...item,
    close:
      item.close && typeof item.close === "string"
        ? Number(item.close)
        : item.close,
    open:
      item.open && typeof item.open === "string"
        ? Number(item.open)
        : item.open,
    high:
      item.high && typeof item.high === "string"
        ? Number(item.high)
        : item.high,
    low: item.low && typeof item.low === "string" ? Number(item.low) : item.low,
  }));

  // 自定义SMA周期
  const indicators = computeSignals(numberOhlc, {
    smaPeriods: smaPeriods,
    emaPeriods: [5, 10, 20, 30, 60],
  });
  const baseIndicator = isStock ? "SMA" : "EMA";
  // const EMA = indicators.map((item) => item.EMA);
  // const DIF = indicators.map((item) => item.DIF);
  // const DEA = indicators.map((item) => item.DEA);
  const latest = indicators[indicators.length - 1] || {};

  // const summaryScore =
  //   (latest.RSI_sig || 0) + (latest.EMA_sig || 0) + (latest.MACD_sig || 0) + (latest.SMA_sig || 0);

  // 获取SMA信号
  // const smaCrossSignal = latest?.SMA_sig; // 交叉信号
  // const smaTrendSignal = latest?.SMA_trend_sig; // 趋势信号

  // 获取SMA值
  // baseIndicator
  const sma5 = latest?.[baseIndicator]?.[5];
  const sma10 = latest?.[baseIndicator]?.[10];
  const sma20 = latest?.[baseIndicator]?.[20];
  const sma30 = latest?.[baseIndicator]?.[30];
  const sma60 = latest?.[baseIndicator]?.[60];
  // console.log('[Stock] indicators indicatorsindicators', indicators)
  // console.log('[Stock] indicators indicators', EMA, DIF, DEA, summaryScore, smaCrossSignal, smaTrendSignal)

  const macd = {
    signal: latest.MACD_sig?.toString() || "0",
    // value: `${latest.DIF?.toFixed(2) || "0"} / ${latest.DEA?.toFixed(2) || "0"}`,
    value: latest.DIF ? latest.DIF.toFixed(isStock ? 2 : 6) : "0",
  };

  const rsi = {
    signal: latest.RSI_sig?.toString() || "0",
    value: latest.RSI?.toFixed(2) || "0",
  };

  const stochasticK = {
    signal: latest?.Stochastic_sig?.toString() || "0",
    value: latest?.StochasticK?.toFixed(2) || "0",
  };

  const smaSignal = {
    [SmaSignal.SMA5_TO_SMA10]: getSmaSignalValue(sma5, sma10),
    [SmaSignal.SMA10_TO_SMA20]: getSmaSignalValue(sma10, sma20),
    [SmaSignal.SMA20_TO_SMA30]: getSmaSignalValue(sma20, sma30),
    [SmaSignal.SMA30_TO_SMA60]: getSmaSignalValue(sma30, sma60),
  };

  const indicatorData = useMemo(() => {
    return [
      {
        id: "1",
        indicator: baseIndicator,
        // signal: "SMA 5 Below SMA10-" + sma5 + "- " + sma10, // SMA 5 = SMA10 SMA 5 above SMA10
        signal: getSmaSignal(
          SmaSignal.SMA5_TO_SMA10,
          smaSignal[SmaSignal.SMA5_TO_SMA10],
          baseIndicator,
        ), // "SMA 5 Below SMA10-" + sma5 + "- " + sma10, // SMA 5 = SMA10 SMA 5 above SMA10
        value: "/",
        sentiment: getTag(smaSignal[SmaSignal.SMA5_TO_SMA10]),
        signalValue: smaSignal[SmaSignal.SMA5_TO_SMA10],
      },
      {
        id: "2",
        indicator: baseIndicator,
        // signal: `SMA 10 Below SMA 20`, //SMA 10 = SMA 20  SMA 10 Above SMA 20
        signal: getSmaSignal(
          SmaSignal.SMA10_TO_SMA20,
          smaSignal[SmaSignal.SMA10_TO_SMA20],
          baseIndicator,
        ),
        value: "/",
        sentiment: getTag(smaSignal[SmaSignal.SMA10_TO_SMA20]),
        signalValue: smaSignal[SmaSignal.SMA10_TO_SMA20],
      },
      {
        id: "3",
        indicator: baseIndicator,
        // signal: `SMA 20 Below SMA 30`,  //SMA 20 = SMA 30  SMA 20 Above SMA 30
        signal: getSmaSignal(
          SmaSignal.SMA20_TO_SMA30,
          smaSignal[SmaSignal.SMA20_TO_SMA30],
          baseIndicator,
        ),
        value: "/",
        sentiment: getTag(smaSignal[SmaSignal.SMA20_TO_SMA30]),
        signalValue: smaSignal[SmaSignal.SMA20_TO_SMA30],
      },
      {
        id: "4",
        indicator: baseIndicator,
        // signal: `SMA 30 Below SMA 60`,  //SMA 30 = SMA 60  SMA 30 Above SMA 60
        signal: getSmaSignal(
          SmaSignal.SMA30_TO_SMA60,
          smaSignal[SmaSignal.SMA30_TO_SMA60],
          baseIndicator,
        ),
        value: "/",
        sentiment: getTag(smaSignal[SmaSignal.SMA30_TO_SMA60]),
        signalValue: smaSignal[SmaSignal.SMA30_TO_SMA60],
      },
      {
        id: "5",
        indicator: "MACD（12、26、9）",
        signal: getMacdSignal(macd.signal),
        value: macd.value || "--",
        sentiment: getTag(macd.signal),
        signalValue: macd.signal,
      },
      {
        id: "6",
        indicator: "RSI",
        signal: getRsiSignal(rsi.signal),
        value: rsi.value,
        sentiment: getTag(rsi.signal),
        signalValue: rsi.signal,
      },
      {
        id: "7",
        indicator: "Stochastic %K(14, 3, 3)",
        // signal: "Stochastic %K>80 Overbought", // Stochastic %K<20 Oversold 20<=Stochastic %K<=80
        signal: getStochasticSignal(stochasticK.signal), // Stochastic %K<20 Oversold 20<=Stochastic %K<=80
        value: stochasticK.value,
        sentiment: getTag(stochasticK.signal),
        signalValue: stochasticK.signal,
      },
    ];
  }, [
    macd,
    rsi,
    baseIndicator,
    smaSignal,
    stochasticK.signal,
    stochasticK.value,
  ]);

  const signalData = useMemo(() => {
    let buySignal = 0;
    let sellSignal = 0;
    let neutralSignal = 0;

    indicatorData.forEach((item) => {
      if (item.signalValue === "1") {
        buySignal++;
      } else if (item.signalValue === "-1") {
        sellSignal++;
      } else if (item.signalValue === "0") {
        neutralSignal++;
      }
    });

    return {
      buySignal,
      sellSignal,
      neutralSignal,
    };
  }, [indicatorData]);

  const signalLevel = useMemo(() => {
    // 总分数 = (Buy数量 × 1 + Neutral数量 × 0 + Sell数量 × -1) / 总指标数量
    // 总指标数量 = Buy + Neutral + Sell
    const signalTotal =
      signalData.buySignal + signalData.neutralSignal + signalData.sellSignal;
    const totalSignalPoints =
      (signalData.buySignal * 1 +
        signalData.neutralSignal * 0 +
        signalData.sellSignal * -1) /
      signalTotal;
    return signalLevelMap(totalSignalPoints);
  }, [signalData]);

  const trendAnalysis = useMemo(() => {
    return trendAnalysisCalc(ohlcv);
  }, [ohlcv]);

  const trendUpDownResult = useMemo(() => {
    const latest = ohlcv?.[ohlcv.length - 1];
    const previous = ohlcv?.[ohlcv.length - 2];
    if (latest?.close >= previous?.close) {
      return "uptrend";
    } else {
      return "downtrend";
    }
  }, [ohlcv]);

  return {
    indicatorData,
    macd,
    rsi,
    stochasticK,
    smaSignal,
    signalData,
    signalLevel,
    trendAnalysis,
    trendUpDownResult,
  };
};
